import React, { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, BrutalCard, Chip } from "../components/brutal";
import { toast } from "sonner";
import { X, ArrowRight, FileText, AlertTriangle, CheckSquare, Square } from "lucide-react";

export default function RecruiterPipelinePage() {
  const [stages, setStages] = useState([]);
  const [refs, setRefs] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkMoving, setBulkMoving] = useState(false);

  const load = async () => {
    const [s, r] = await Promise.all([api.get("/stages"), api.get("/referrals")]);
    setStages(s.data);
    setRefs(r.data);
    setSelected(new Set());
  };

  useEffect(() => { load(); }, []);

  const toggleSelect = (id, e) => {
    e?.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const bulkMove = async (stageId) => {
    if (selected.size === 0) return;
    const stage = stages.find((s) => s.id === stageId);
    if (!window.confirm(`Move ${selected.size} referral${selected.size === 1 ? "" : "s"} to "${stage?.name}"? Points will be awarded automatically.`)) return;
    setBulkMoving(true);
    try {
      const { data } = await api.post("/referrals/bulk-stage", { referral_ids: Array.from(selected), stage_id: stageId });
      toast.success(`Moved ${data.moved} referral${data.moved === 1 ? "" : "s"} to ${data.stage}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkMoving(false);
    }
  };

  const move = async (ref, stageId) => {
    try {
      await api.patch(`/referrals/${ref.id}/stage`, { stage_id: stageId });
      toast.success("Stage updated");
      await load();
      setDrawer(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reject = async (ref) => {
    if (!window.confirm("Reject this referral? Points will be reversed.")) return;
    try {
      await api.post(`/referrals/${ref.id}/reject`, { reason: "withdrawn/disqualified" });
      toast.success("Referral rejected");
      await load();
      setDrawer(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const stageCols = stages.filter((s) => !s.is_rejected);
  const selectedCount = selected.size;

  return (
    <div>
      <PageHeader
        title="Pipeline"
        subtitle="Move candidates through stages — points are awarded to referrers automatically."
      />

      {selectedCount > 0 && (
        <div
          className="sticky top-0 z-40 mb-4 bg-brand-yellow border-2 border-black rounded-2xl p-3 shadow-brutal flex flex-wrap items-center gap-3"
          data-testid="bulk-action-bar"
        >
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-black text-brand-yellow rounded-lg flex items-center justify-center font-display font-black text-sm" data-testid="bulk-count">
              {selectedCount}
            </div>
            <span className="font-semibold text-sm">selected</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 flex-1">
            <span className="text-xs font-bold uppercase tracking-widest hidden sm:inline">Move to:</span>
            {stageCols.map((s) => (
              <button
                key={s.id}
                onClick={() => bulkMove(s.id)}
                disabled={bulkMoving}
                className="btn-brutal btn-white text-xs disabled:opacity-50"
                data-testid={`bulk-move-${s.name.replace(/\s+/g, "-")}`}
              >
                {s.name} <span className="text-[10px] text-neutral-600 ml-1">+{s.points}</span>
              </button>
            ))}
          </div>
          <button
            onClick={() => setSelected(new Set())}
            className="btn-brutal bg-black text-white text-xs"
            data-testid="bulk-clear"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        </div>
      )}

      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-board">
        {stageCols.map((s, idx) => {
          const items = refs.filter((r) => r.current_stage_id === s.id && r.status !== "rejected");
          const colors = ["bg-white", "bg-brand-yellow", "bg-brand-blue/20", "bg-brand-blue/30", "bg-brand-orange/30", "bg-brand-green/30", "bg-brand-green"];
          return (
            <div key={s.id} className="min-w-[290px] w-[290px]">
              <div className={`border-2 border-black rounded-2xl ${colors[idx % colors.length]} p-3 shadow-brutal`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-display font-black text-lg leading-none">{s.name}</div>
                    <div className="text-[11px] uppercase tracking-widest font-bold text-neutral-700">+{s.points} pts</div>
                  </div>
                  <div className="w-8 h-8 border-2 border-black rounded-xl bg-white flex items-center justify-center font-display font-black">
                    {items.length}
                  </div>
                </div>
                <div className="space-y-2.5 min-h-[100px]">
                  {items.map((r) => {
                    const isSelected = selected.has(r.id);
                    return (
                      <div
                        key={r.id}
                        onClick={() => setDrawer(r)}
                        className={`relative w-full text-left border-2 border-black rounded-xl bg-white p-3 hover:-translate-y-0.5 transition-transform shadow-[2px_2px_0_0_#09090B] block cursor-pointer ${isSelected ? "ring-2 ring-brand-orange ring-offset-1" : ""}`}
                        data-testid={`pipeline-card-${r.id}`}
                      >
                        <button
                          type="button"
                          onClick={(e) => toggleSelect(r.id, e)}
                          className={`absolute top-2 right-2 w-6 h-6 border-2 border-black rounded-md flex items-center justify-center ${isSelected ? "bg-brand-orange text-white" : "bg-white"}`}
                          data-testid={`pipeline-select-${r.id}`}
                          aria-label={isSelected ? "Deselect referral" : "Select referral"}
                        >
                          {isSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                        </button>
                        <div className="pr-8">
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-sm truncate">{r.candidate_name}</span>
                            {r.duplicate_of && (
                              <span
                                className="inline-flex items-center gap-0.5 bg-red-100 border border-red-500 rounded-full px-1.5 py-0.5 text-[9px] font-bold text-red-700 uppercase tracking-wide flex-shrink-0"
                                title="This candidate has an active referral on another role"
                                data-testid={`pipeline-duplicate-${r.id}`}
                              >
                                <AlertTriangle className="w-2.5 h-2.5" /> Dup
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-neutral-600 truncate">{r.job_title}</div>
                          <div className="flex items-center gap-2 mt-2">
                            <img src={r.employee_avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(r.employee_name || "E")}`} alt="" className="w-5 h-5 rounded-full border border-black object-cover" />
                            <span className="text-[11px] text-neutral-600 truncate">by {r.employee_name}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {drawer && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setDrawer(null)}>
          <div className="flex-1 bg-black/40" />
          <div
            className="w-full max-w-md bg-white border-l-2 border-black p-6 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="pipeline-drawer"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-black text-2xl">{drawer.candidate_name}</h2>
              <button onClick={() => setDrawer(null)} className="w-9 h-9 border-2 border-black rounded-xl flex items-center justify-center" data-testid="drawer-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            {drawer.duplicate_of && (
              <div
                className="flex items-start gap-2 border-2 border-red-500 bg-red-50 rounded-xl p-3 mb-3"
                data-testid="drawer-duplicate-warning"
              >
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-red-800">
                  <strong>Heads up —</strong> this candidate has an active referral on another open role. Double-check before moving them forward.
                </div>
              </div>
            )}
            <div className="space-y-2 text-sm">
              <div><span className="font-bold uppercase tracking-widest text-[11px]">Email:</span> {drawer.candidate_email}</div>
              <div><span className="font-bold uppercase tracking-widest text-[11px]">Phone:</span> {drawer.candidate_phone || "—"}</div>
              <div><span className="font-bold uppercase tracking-widest text-[11px]">Job:</span> {drawer.job_title}</div>
              <div><span className="font-bold uppercase tracking-widest text-[11px]">Referred by:</span> {drawer.employee_name}</div>
              <div><span className="font-bold uppercase tracking-widest text-[11px]">Stage:</span> <Chip color="bg-brand-yellow">{drawer.current_stage_name}</Chip></div>
              <div><span className="font-bold uppercase tracking-widest text-[11px]">Status:</span> {drawer.status}</div>
              {drawer.cv_filename && (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${drawer.cv_path}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 btn-brutal btn-white mt-2"
                  data-testid="download-cv"
                >
                  <FileText className="w-4 h-4" /> {drawer.cv_filename}
                </a>
              )}
              <div>
                <div className="font-bold uppercase tracking-widest text-[11px] mt-3">Note</div>
                <div className="text-sm text-neutral-700">{drawer.note || "—"}</div>
              </div>
            </div>

            <div className="mt-6">
              <div className="font-display font-black text-lg mb-2">Move to stage</div>
              <div className="grid grid-cols-1 gap-2">
                {stageCols.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => move(drawer, s.id)}
                    disabled={s.id === drawer.current_stage_id}
                    className={`flex items-center justify-between border-2 border-black rounded-xl px-3 py-2 ${
                      s.id === drawer.current_stage_id ? "bg-neutral-200 cursor-not-allowed" : "bg-white hover:bg-brand-yellow"
                    }`}
                    data-testid={`move-${s.name.replace(/\s+/g, "-")}`}
                  >
                    <span className="font-semibold text-sm">{s.name}</span>
                    <span className="text-xs font-bold">+{s.points} <ArrowRight className="w-3 h-3 inline" /></span>
                  </button>
                ))}
              </div>
            </div>

            {drawer.status !== "rejected" && (
              <button
                onClick={() => reject(drawer)}
                className="btn-brutal w-full mt-6 bg-red-500 text-white"
                data-testid="reject-referral"
              >
                Reject & reverse points
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
